import React, { Fragment, useEffect, useMemo } from "react";
import clsx from "clsx";
import { showError } from "../../../../utils/toast.ts";
import { Card } from "../../../shared/card/card.tsx";
import { Button, TextArea, TimePeriodSelect, type ButtonVariant } from "../../../ui/index.ts";
import { Spinner } from "../../../ui/spinner/spinner.tsx";
import { useProbationQuestions } from "../hooks/use-probation-questions.ts";
import { useProbationForm } from "../hooks/use-probation-form.ts";
import { QuestionRenderer } from "./index.tsx";
import type { ProbationQuestion, SectionType } from "../schemas/index.ts";
import type { ProbationFormState } from "../hooks/use-probation-form.ts";
import {
  getHrSubmitButtonConfig,
  isHrExtendRecommendation,
} from "../utils/hr-submit-button.ts";
import {
  isInductionYes,
  isSqeInductionDurationQuestion,
  SQE_INDUCTION_QUESTION_ID,
} from "../utils/sqe-induction-questions.ts";
import { sqeManagerRatingPercentage } from "../utils/sqe-manager-rating.ts";
import styles from "./probation-form-panel.module.css";

export interface ProbationFormPanelProps {
  cycleId: number;
  sectionType: SectionType;
  onSuccess: () => void;
  transformQuestions?: (questions: ProbationQuestion[]) => ProbationQuestion[];
  conditionalInductionDuration?: boolean;
}

export function ProbationFormPanel({ cycleId, sectionType, onSuccess, transformQuestions, conditionalInductionDuration = false }: ProbationFormPanelProps) {
  const { questions: fetchedQuestions, loading, error, refetch } = useProbationQuestions({ cycleId, sectionType });
  const questions = useMemo(
    () => transformQuestions ? transformQuestions(fetchedQuestions) : fetchedQuestions,
    [fetchedQuestions, transformQuestions],
  );
  const {
    form,
    isSubmitting,
    state,
    recommendationQuestionIndex,
    handleSaveDraft,
    handleSubmit,
  } = useProbationForm({
    questions,
    cycleId,
    sectionType,
    conditionalInductionDuration,
    onSuccess: () => {
      void refetch();
      onSuccess();
    },
  });

  useEffect(() => {
    if (error) showError(error);
  }, [error]);

  if (loading) {
    return <Spinner />;
  }

  const showHrToolbar = sectionType === "HR" && recommendationQuestionIndex >= 0;
  const inductionQuestionIndex = conditionalInductionDuration
    ? questions.findIndex((question) => question.questionId === SQE_INDUCTION_QUESTION_ID)
    : -1;
  const durationQuestionIndex = conditionalInductionDuration
    ? questions.findIndex(isSqeInductionDurationQuestion)
    : -1;

  return (
    <div className={styles.wrapper}>
      {isSubmitting && <Spinner variant="overlay" />}
      {/* {state === "submitted" && <Toolbar state={state} />} */}
      <Card className={styles.formCard}>
        <div className={clsx(styles.form, state === "submitted" && styles.formSubmitted)}>
          <form.Field
            name="answers"
            children={(answersField) => (
              <form.Subscribe
                selector={(formState) => inductionQuestionIndex >= 0
                  ? formState.values.answers[inductionQuestionIndex]?.answer
                  : undefined}
              >
                {(inductionAnswer) => answersField.state.value.map((_, index) => {
                  const showDuration = isInductionYes(inductionAnswer);
                  if (index === durationQuestionIndex && !showDuration) return null;
                  const displayIndex = index + 1 - (
                    durationQuestionIndex >= 0 && index > durationQuestionIndex && !showDuration ? 1 : 0
                  );
                  return (
                  <Fragment key={questions[index].reviewId}>
                    <form.Field
                      name={`answers[${index}].answer`}
                      children={(field) => (
                        <QuestionRenderer question={questions[index]} field={field} index={displayIndex} />
                      )}
                    />
                    {sectionType === "SQE_MANAGER" ? (
                      <form.Field name={`answers[${index}].textAnswers`}>
                        {(field) => (
                          <TextArea
                            id={`sqe-manager-comment-${questions[index].reviewId}`}
                            label={`Comments for ${questions[index].question}`}
                            value={field.state.value ?? ""}
                            onChange={(event) => field.handleChange(event.target.value)}
                            onBlur={field.handleBlur}
                            readOnly={!questions[index].editable}
                            placeholder="Enter your comments..."
                          />
                        )}
                      </form.Field>
                    ) : null}
                    {sectionType === "HR" &&
                      index === recommendationQuestionIndex &&
                      state !== "submitted" && (
                        <form.Subscribe
                          selector={(formState) =>
                            formState.values.answers[recommendationQuestionIndex]?.answer as
                              | string
                              | undefined
                          }
                        >
                          {(recommendationAnswer) =>
                            isHrExtendRecommendation(recommendationAnswer) ? (
                              <div className={styles.extensionPeriodWrapper}>
                                <form.Field name="extensionPeriod">
                                  {(field) => (
                                    <>
                                      <label
                                        className={clsx("sub-heading-md", styles.extensionLabel)}
                                        htmlFor="probation-hr-extension-period-amount"
                                      >
                                        Probation Extension Period
                                        <span className="text-danger text-nowrap" aria-hidden>
                                          {"\u00A0*"}
                                        </span>
                                      </label>
                                      <TimePeriodSelect
                                        id="probation-hr-extension-period"
                                        value={field.state.value ?? { amount: 1, unit: "month" }}
                                        onChange={(value) => field.handleChange(value)}
                                        error={
                                          field.state.meta.errors?.[0]?.message?.toString() ??
                                          undefined
                                        }
                                      />
                                    </>
                                  )}
                                </form.Field>
                              </div>
                            ) : null
                          }
                        </form.Subscribe>
                      )}
                  </Fragment>
                  );
                })}
              </form.Subscribe>
            )}
          />
        </div>
        {sectionType === "SQE_MANAGER" ? (
          <form.Subscribe selector={(formState) => formState.values.answers}>
            {(answers) => (
              <div className={styles.ratingSummary}>
                <span>Overall Rating</span>
                <strong>{sqeManagerRatingPercentage(questions, answers)}%</strong>
              </div>
            )}
          </form.Subscribe>
        ) : null}
      </Card>
      {showHrToolbar ? (
        <form.Subscribe
          selector={(formState) =>
            formState.values.answers[recommendationQuestionIndex]?.answer as string | undefined
          }
        >
          {(recommendationAnswer) => (
            <Toolbar
              state={state}
              showCTAs={state !== "submitted"}
              onSaveDraft={handleSaveDraft}
              onSubmit={handleSubmit}
              submitButton={getHrSubmitButtonConfig(recommendationAnswer)}
            />
          )}
        </form.Subscribe>
      ) : (
        <Toolbar
          state={state}
          showCTAs={state !== "submitted"}
          onSaveDraft={handleSaveDraft}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
}

interface ToolbarProps {
  state: ProbationFormState;
  showCTAs?: boolean;
  onSaveDraft?: () => void;
  onSubmit?: () => void;
  submitButton?: { label: string; variant: ButtonVariant };
}

function Toolbar({
  state,
  showCTAs = false,
  onSaveDraft,
  onSubmit,
  submitButton = { label: "Submit", variant: "accent" },
}: ToolbarProps) {
  let statusLabel = "";
  let statusIndicator = "";
  let toolBarStyle = "";

  switch (state) {
    case "submitted":
      statusLabel = "Probation Assessment Submitted";
      statusIndicator = styles.indicatorSuccess;
      toolBarStyle = styles.toolbarSuccess;
      break;
    case "draft":
      statusLabel = "Draft Saved";
      statusIndicator = styles.indicatorDraft;
      toolBarStyle = styles.toolbarDraft;
      break;
    default:
      statusLabel = "Not Filled";
      statusIndicator = styles.indicatorInitial;
      toolBarStyle = styles.toolbarInitial;
      break;
  }

  return (
    <Card className={clsx(styles.toolbar, toolBarStyle)}>
      <div>
        {statusLabel && (
          <div className={styles.statusWrapper}>
            <span className={clsx(styles.indicator, statusIndicator)} />
            <p className="heading-xs">{statusLabel}</p>
          </div>
        )}
      </div>
      {showCTAs && onSaveDraft && onSubmit && (
        <div className={styles.actionWrapper}>
          <Button onClick={onSaveDraft} variant="muted-outline">
            Save Draft
          </Button>
          <Button onClick={onSubmit} variant={submitButton.variant}>
            {submitButton.label}
          </Button>
        </div>
      )}
    </Card>
  );
}
